import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Loader2 } from 'lucide-react';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface LoginPageProps {
  onLogin: (user: AuthUser, token: string) => void;
}

/** Errors surfaced by /api/auth/entra/callback as ?error=<code>. */
const ENTRA_ERRORS: Record<string, string> = {
  no_access: 'That Microsoft account does not have access to the finance dashboard.',
  account_disabled: 'That account has been deactivated.',
  identity_mismatch: 'That Microsoft account is linked to a different user.',
  session_expired: 'Sign-in took too long. Please try again.',
  no_email_in_token: 'Microsoft did not return an email address for that account.',
  exchange_failed: 'Microsoft sign-in failed. Please try again.',
  server_misconfigured: 'Sign-in is not fully configured. Contact your administrator.',
};

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [step, setStep] = useState<'credentials' | 'twoFactor'>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Surface an error passed back by the Entra callback redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (!err) return;
    setError(ENTRA_ERRORS[err] ?? 'Sign-in failed. Please try again.');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const submitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Sign-in failed');
        return;
      }
      if (data.status === '2fa_required') {
        setPendingUserId(data.userId);
        setStep('twoFactor');
        return;
      }
      onLogin(data.user, '');
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ userId: pendingUserId, code, rememberDevice }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Verification failed');
        // An exhausted or expired code can only be replaced by a new one.
        if (data.code === 'too_many_attempts' || data.code === 'expired') {
          setStep('credentials');
          setCode('');
          setPendingUserId(null);
        }
        return;
      }
      onLogin(data.user, '');
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const startEntra = () => {
    const returnTo = encodeURIComponent(window.location.pathname || '/');
    window.location.href = `/api/auth/entra/start?returnTo=${returnTo}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
      <Card className="w-full max-w-[400px]">
        <CardHeader className="text-center pb-2">
          <img src="/logo.svg" alt="Logo" className="h-10 mx-auto mb-4 logo-themed" />
          <h1 className="text-xl font-bold">Financial Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {step === 'credentials'
              ? 'Sign in to access the dashboard'
              : 'Enter the code we emailed you'}
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-md p-2">{error}</p>
          )}

          {step === 'credentials' ? (
            <>
              <form onSubmit={submitCredentials} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={busy}>
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Sign in
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <Button variant="outline" className="w-full" onClick={startEntra} disabled={busy}>
                Sign in with Microsoft
              </Button>
            </>
          ) : (
            <form onSubmit={submitCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">6-digit code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  disabled={busy}
                  className="text-center text-lg tracking-[0.4em]"
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberDevice}
                  onCheckedChange={(v) => setRememberDevice(v === true)}
                  disabled={busy}
                />
                <Label htmlFor="remember" className="text-sm font-normal">
                  Trust this device for today
                </Label>
              </div>

              <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Verify
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                disabled={busy}
                onClick={() => {
                  setStep('credentials');
                  setCode('');
                  setError('');
                  setPendingUserId(null);
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
